import {
  Checkbox,
  List,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  ButtonProps,
  Card,
  CardHeader,
  Divider,
} from '@mui/material';
import { useState, useCallback } from 'react';

import { intersection, not } from '../helpers';
import { TransferListProps } from '../TransferList';
import { TransferListItem } from '../types';

export const useTransferList = ({
  id,
  onChange,
  leftItems,
  rightItems = [],
  listSx = {},
  titleTypographyProps = {},
  subheaderTypographyProps = {},
  listItemTypographyProps = {},
}: TransferListProps) => {
  const [checked, setChecked] = useState<TransferListItem[]>([]);
  const [left, setLeft] = useState(leftItems);
  const [right, setRight] = useState(rightItems);

  // Derived state for checked items on each side
  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const numberOfChecked = (items: TransferListItem[]) => intersection(checked, items).length;

  /**
   * Toggles the checked state of an item.
   */
  const handleToggle = useCallback(
    (item: TransferListItem) => () => {
      setChecked((prevChecked) =>
        prevChecked.some((checkedItem) => checkedItem.id === item.id)
          ? prevChecked.filter((checkedItem) => checkedItem.id !== item.id)
          : [...prevChecked, item]
      );
    },
    []
  );

  const moveAllToRight = useCallback(() => {
    setRight((prevRight) => {
      const newRight = [...prevRight, ...left];
      onChange([], newRight);
      return newRight;
    });

    setLeft([]);
    setChecked([]);
  }, [left, onChange]);

  const moveSelectedToRight = useCallback(() => {
    const newLeft = not(left, leftChecked);
    setRight((prevRight) => {
      const newRight = [...prevRight, ...leftChecked];
      onChange(newLeft, newRight);
      return newRight;
    });

    setLeft(newLeft);
    setChecked(not(checked, leftChecked));
  }, [left, leftChecked, checked, onChange]);

  const moveAllToLeft = useCallback(() => {
    setLeft((prevLeft) => {
      const newLeft = [...prevLeft, ...right];
      onChange(newLeft, []);
      return newLeft;
    });

    setRight([]);
    setChecked([]);
  }, [onChange, right]);

  const moveSelectedToLeft = useCallback(() => {
    const newRight = not(right, rightChecked);
    setLeft((prevLeft) => {
      const newLeft = [...prevLeft, ...rightChecked];
      onChange(newLeft, newRight);
      return newLeft;
    });

    setRight(newRight);
    setChecked(not(checked, rightChecked));
  }, [right, rightChecked, checked, onChange]);

  /**
   * Renders a custom list for either side of the transfer list.
   */
  const renderList = (title: string, items: TransferListItem[]) => (
    <Card sx={{ minWidth: 320, height: 375, overflow: 'auto', ...listSx }}>
      <CardHeader
        title={title}
        subheader={`${numberOfChecked(items)}/${items.length} selected`}
        titleTypographyProps={{
          variant: 'body1',
          color: 'text.primary',
          ...titleTypographyProps,
        }}
        subheaderTypographyProps={{
          variant: 'body2',
          color: 'text.secondary',
          ...subheaderTypographyProps,
        }}
      />

      <Divider />

      <List dense component="div" role="list">
        {items.map((item) => {
          const labelId = `transfer-list-item-${id}-${item.value}-label`;
          return (
            <ListItemButton
              key={item.id}
              role="listitem"
              onClick={handleToggle(item)}
              sx={{ cursor: 'pointer' }}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checked.includes(item)}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ 'aria-labelledby': labelId }}
                />
              </ListItemIcon>
              <ListItemText
                id={labelId}
                primary={item.value}
                primaryTypographyProps={{
                  variant: 'body2',
                  ...listItemTypographyProps,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );

  const baseButtonProps: ButtonProps = {
    sx: { my: 0.5 },
    variant: 'outlined',
    size: 'small',
  };

  return {
    left,
    leftChecked,
    moveAllToLeft,
    moveSelectedToLeft,

    right,
    rightChecked,
    moveAllToRight,
    moveSelectedToRight,

    baseButtonProps,
    renderList,
    handleToggle,
  };
};
